const REGION_PREFIX_MAP = {
  AS: 'Asia',
  EU: 'Europe',
  AF: 'Africa',
  NA: 'North America',
  SA: 'South America',
  OC: 'Oceania',
  GL: 'Global'
}

const ISO_COUNTRY_CODE_REGEX = /^[A-Z]{2}$/
const REGION_PACK_REGEX = /^([A-Z]{2})(\d+)$/

const toIntlLocale = (locale) => {
  const intlLocaleMap = {
    zh: 'zh-CN',
    'zh-HK': 'zh-HK',
    en: 'en-US',
    de: 'de-DE',
    es: 'es-ES',
    ja: 'ja-JP',
    th: 'th-TH',
    tr: 'tr-TR',
    ko: 'ko-KR'
  }
  return intlLocaleMap[locale] || locale || 'en-US'
}

const getIntlRegionName = (countryCode, locale) => {
  try {
    const dn = new Intl.DisplayNames([toIntlLocale(locale)], { type: 'region' })
    return dn.of(countryCode) || countryCode
  } catch (error) {
    return countryCode
  }
}

export const getPrimaryCountryCode = (rawCode) => {
  if (!rawCode) {
    return null
  }
  const upperCode = String(rawCode).trim().toUpperCase()
  if (ISO_COUNTRY_CODE_REGEX.test(upperCode)) {
    return upperCode
  }
  const parts = upperCode.split('-').map((part) => part.trim()).filter(Boolean)
  const firstIsoCode = parts.find((part) => ISO_COUNTRY_CODE_REGEX.test(part))
  return firstIsoCode || null
}

export const getFlagCountryCode = (rawCode) => {
  if (!rawCode) {
    return 'UN'
  }
  const upperCode = String(rawCode).trim().toUpperCase()

  // Region packs like AS20 / EU27 should use multi-region icon.
  if (REGION_PACK_REGEX.test(upperCode)) {
    return 'UN'
  }

  // Multi-region codes split by "-" should use multi-region icon
  // when there is more than one ISO country code.
  const parts = upperCode.split('-').map((part) => part.trim()).filter(Boolean)
  const isoCodes = parts.filter((part) => ISO_COUNTRY_CODE_REGEX.test(part))
  if (isoCodes.length > 1) {
    return 'UN'
  }

  return getPrimaryCountryCode(upperCode) || 'UN'
}

export const isRegionPackCode = (rawCode) => {
  if (!rawCode) {
    return false
  }
  const upperCode = String(rawCode).trim().toUpperCase()
  return REGION_PACK_REGEX.test(upperCode)
}

export const getCountryRegionDisplayName = ({
  code,
  locale,
  t,
  te
}) => {
  if (!code) {
    return t ? t('Common_Unknown') : 'Unknown'
  }

  const normalized = String(code).trim()
  const upperCode = normalized.toUpperCase()

  if (te && te(normalized)) {
    return t(normalized)
  }

  const regionPackMatch = upperCode.match(REGION_PACK_REGEX)
  if (regionPackMatch) {
    const [, regionPrefix, countryCount] = regionPackMatch
    const regionName = REGION_PREFIX_MAP[regionPrefix] || regionPrefix
    return `${regionName} ${countryCount} Countries`
  }

  if (upperCode.includes('-')) {
    const areaCodes = upperCode.split('-').map((part) => part.trim()).filter(Boolean)
    const isoCodes = areaCodes.filter((part) => ISO_COUNTRY_CODE_REGEX.test(part))
    if (isoCodes.length > 1) {
      return isoCodes
        .map((countryCode) => getIntlRegionName(countryCode, locale))
        .join(' / ')
    }
  }

  if (ISO_COUNTRY_CODE_REGEX.test(upperCode)) {
    return getIntlRegionName(upperCode, locale)
  }

  const primaryCode = getPrimaryCountryCode(upperCode)
  if (primaryCode) {
    return getIntlRegionName(primaryCode, locale)
  }

  return normalized
}
